import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { downloadUserConversations } from '@/store/dashboard/actions'

import { useTranslation } from 'react-i18next'
import { Box, Button, Stack, Typography } from '@mui/material'
import DashboardService from '@/services/DashboardService'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import dayjs from 'dayjs'

const dashboardService = new DashboardService()

interface ConversationLog {
  _id: string
  sender: string
  message: string
  date: string
  time: string
}

interface UserConversationsProps {
  sessionId: string
}

function UserConversations({ sessionId }: UserConversationsProps) {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [allConversations, setAllConversations] = useState<ConversationLog[]>(
    [],
  )
  const [conversationCount, setConversationCount] = useState(0)

  const columns: GridColDef<ConversationLog>[] = [
    {
      headerName: t('Message'),
      field: 'message',
      sortable: true,
      renderCell: params => {
        const { value } = params
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: value || '',
            }}
          />
        )
      },
      flex: 1,
    },
    {
      headerName: t('Sender'),
      field: 'sender',
      sortable: true,
      width: 150,
    },
    {
      headerName: t('Date'),
      field: 'date',
      sortable: true,
      valueFormatter: value =>
        dayjs.utc(value).local().format('DD/MM/YYYY hh:mm A'),
      width: 180,
    },
  ]

  useEffect(() => {
    const func = async () => {
      try {
        setLoading(true)
        const data = await dashboardService.getUserConversations({
          chat_session_id: sessionId,
        })

        const updateData: any[] = []
        data.conversation_logs.forEach((d: any) => {
          if (d.reply) {
            updateData.push({
              ...d,
              key: `${d._id}_AGENT`,
              sender: 'AI Assistant',
              message: d.reply.text ? d.reply.text : '',
              time: d.responded_time,
              date: d.responded_time,
            })
          }
          if (d.question || (d.question && d.question.event)) {
            updateData.push({
              ...d,
              key: `${d._id}_USER`,
              sender: 'Learner',
              message: d.question.text ? d.question.text : '',
              time: d.questioned_time,
              date: d.questioned_time,
            })
          }
        })
        setAllConversations(updateData.reverse())
        setConversationCount(data.conversation_logs_count)
        setLoading(false)
      } catch (error: any) {
        setLoading(false)
      }
    }

    !loading && func()
  }, [sessionId])

  const exportReport = async () => {
    try {
      const blob: any = await dispatch(
        downloadUserConversations({
          chat_session_id: sessionId,
        }),
      )

      if (!blob) {
        return
      }

      const extension =
        blob?.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          ? 'xlsx'
          : 'csv'
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `nf_report_conversations_${sessionId}.${extension}`,
      )
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {}
  }

  return (
    <div className="tw-border tw-rounded-2xl tw-shadow-md tw-p-8">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" component="h2">
          {t('Conversations')}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        my={2}
      >
        <h3 className="mb-0">
          {t('Total conversation turns:')} {conversationCount}
        </h3>
        <Button onClick={exportReport}>{t('Download Conversation')}</Button>
      </Stack>

      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          getRowId={row => row._id + row.sender}
          rows={allConversations}
          columns={columns}
          loading={loading}
        />
      </Box>
    </div>
  )
}

export default UserConversations
